import {
  Component,
  ElementRef,
  inject,
  Input,
  OnChanges,
  Pipe,
  PipeTransform,
  SimpleChanges,
  ChangeDetectorRef,
  input,
  viewChild,
  output,
} from '@angular/core';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NzCheckboxModule } from 'ng-zorro-antd/checkbox';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { MessageApiService, Message, MessageThreadOpenedEvent, MessageThreadQuery } from '../../message.api.service';
import { MessageComponent } from '../message/message.component';
import { MessageExerciseDetailComponent } from '../exercise-detail/exercise-detail.component';

/**
 * 解析消息的引用父消息：在给定消息列表中按 parentId 查找对应消息
 * 未命中或非引用消息返回 null（模板不渲染引用块）
 */
@Pipe({
  name: 'messageParent',
  standalone: true,
})
export class MessageParentPipe implements PipeTransform {
  transform(list: Message[] | undefined, message: Message): Message | null {
    if (!list || !message?.parentId) return null;
    return list.find(m => m.id === message.parentId) ?? null;
  }
}

/**
 * 通用消息流底座（任务无关）
 *
 * 领域模型见 message/README.md：
 * - 输入 threadQuery{threadId, taskId?, moduleId?, paperQuestionId?}，感知变化自动重拉该 thread 消息流
 * - IM 化消息流：page=1 为最新一页（从新往旧翻），消息按 createAt 升序展示（旧上新下）；
 *   进入会话自动滚动到底（最新）；向上滑到顶触发加载更早历史（前插并保持滚动位置不跳）
 * - 渲染 fl-message（自带 left/right、标签、已读环、引用）
 * - 回复/打标交互由上层负责
 */
@Component({
  selector: 'fl-message-list',
  templateUrl: './message-list.component.html',
  styleUrls: ['./message-list.component.scss'],
  standalone: true,
  imports: [
    NzSpinModule,
    NzCheckboxModule,
    NzButtonModule,
    NzIconModule,
    MessageComponent,
    MessageParentPipe,
    MessageExerciseDetailComponent,
  ],
})
export class MessageListComponent implements OnChanges {
  /** 消息流查询描述 */
  readonly threadQuery = input<MessageThreadQuery>(undefined);

  /** 展示的消息流（按 createAt 升序：旧在上、新在下） */
  messages: Message[] = [];

  /** 是否已加载完所有历史（到顶无更多） */
  reachedTop = false;

  /** 加载中 */
  loading = false;

  /** 顶部加载更早历史中 */
  loadingMore = false;

  /** 每次拉取完成后的消息流变化（供外层聚合标签统计） */
  readonly messagesChange = output<Message[]>();

  /** 点击头像回复（向上抛被回复消息） */
  readonly avatarClick = output<Message>();

  /** 当前 thread 首屏获取成功；获取成功即视为已读 */
  readonly threadOpened = output<MessageThreadOpenedEvent>();

  /** 回复后刷新信号：数值变化即重拉当前 thread 最新页 */
  readonly refreshTick = input<number>(undefined);

  /** 仅展示教师未回复的消息（后端 unreplied=true 过滤） */
  @Input() unreplied = false;

  /** 当前正在回复的消息 id；命中的消息右侧展示发送时间 */
  @Input() replyMessageId?: number | null;

  /** 是否启用批量选择（默认关闭，不启用时行为与旧版完全一致） */
  readonly selectable = input(false);

  /** 是否处于批量选择模式（进入后每条消息左侧显示 checkbox） */
  selectionMode = false;

  /** 批量模式下已勾选的消息 id 集合 */
  selectedIds = new Set<number>();

  /** 全选态是否已勾满 */
  get allSelected(): boolean {
    return this.messages.length > 0 && this.selectedIds.size === this.messages.length;
  }

  /** 滚动容器 */
  private readonly scrollEl = viewChild.required<ElementRef<HTMLElement>>('scrollWrap');

  /** 当前已加载页数（page=1 最新） */
  private page = 1;

  /** 当前 thread 的 id（避免同一 thread 反复重拉 + 派生 loadSeq） */
  private lastThreadId?: number;

  /** 递增请求序号，丢弃过期响应（快速切题/切任务时不串） */
  private loadSeq = 0;

  private messageApiSrv = inject(MessageApiService);
  private cdr: ChangeDetectorRef = inject(ChangeDetectorRef);

  ngOnChanges(changes: SimpleChanges) {
    const q = changes['threadQuery']?.currentValue as MessageThreadQuery | undefined;
    if (q && q.threadId !== this.lastThreadId) {
      this.lastThreadId = q.threadId;
      this.reload(q);
      return;
    }
    // refreshTick 变化：重置分页重拉最新页
    const threadQuery = this.threadQuery();
    if (changes['refreshTick'] && threadQuery) {
      this.reload(threadQuery);
      return;
    }
    // 切换「全部/未回复」：按同一 thread 重新拉取，重置分页
    if (changes['unreplied'] && threadQuery) {
      this.reload(threadQuery);
    }
  }

  /** 切题/刷新：重置分页，拉最新页并滚到底 */
  private reload(query: MessageThreadQuery) {
    const seq = ++this.loadSeq;
    this.page = 1;
    this.reachedTop = false;
    this.loading = true;
    this.loadingMore = false;
    this.exitSelectionMode();
    this.messageApiSrv
      .getThreadMessages(query.threadId, {
        paperQuestionId: query.paperQuestionId,
        moduleId: query.moduleId ?? undefined,
        page: 1,
        unreplied: this.unreplied ? true : undefined,
      })
      .then(res => {
        if (seq !== this.loadSeq) return;
        this.messages = this.sortAsc(res.results ?? []);
        this.reachedTop = (res.count ?? 0) <= this.messages.length;
        this.messagesChange.emit(this.messages);
        this.loading = false;
        this.threadOpened.emit({ query });
        // OnPush：请求续体非模板事件，消息流/加载态需主动标记
        this.cdr.markForCheck();
        // 进入会话：滚到底部（最新）
        this.scrollToBottom();
      })
      .catch(() => {
        if (seq === this.loadSeq) {
          this.loading = false;
          // OnPush：请求失败续体非模板事件，loading 需主动标记
          this.cdr.markForCheck();
        }
      });
  }

  /** 滚动到最底部（最新） */
  private scrollToBottom() {
    requestAnimationFrame(() => {
      const el = this.scrollEl()?.nativeElement;
      if (el) el.scrollTop = el.scrollHeight;
    });
  }

  /** 向上滑到顶 → 加载更早一页（前插历史，保持滚动位置不跳） */
  onScroll() {
    const el = this.scrollEl()?.nativeElement;
    if (!el || el.scrollTop > 0) return;
    const threadQuery = this.threadQuery();
    if (this.loading || this.loadingMore || this.reachedTop || !threadQuery) return;
    const prevScrollHeight = el.scrollHeight;
    const seq = ++this.loadSeq;
    const nextPage = this.page + 1;
    this.loadingMore = true;
    this.messageApiSrv
      .getThreadMessages(threadQuery.threadId, {
        paperQuestionId: threadQuery.paperQuestionId,
        moduleId: threadQuery.moduleId ?? undefined,
        page: nextPage,
        unreplied: this.unreplied ? true : undefined,
      })
      .then(res => {
        if (seq !== this.loadSeq) return;
        const older = this.sortAsc(res.results ?? []);
        // 更早历史在顶部
        this.messages = [...older, ...this.messages].sort((a, b) => this.timeOf(a) - this.timeOf(b));
        this.page = nextPage;
        this.reachedTop = (res.count ?? 0) <= this.messages.length;
        this.loadingMore = false;
        this.messagesChange.emit(this.messages);
        // OnPush：请求续体非模板事件，前插的历史消息/加载态需主动标记
        this.cdr.markForCheck();
        // 保持当前可见位置不因顶部插入而跳动：把 scrollTop 补上前插高度
        requestAnimationFrame(() => {
          el.scrollTop = el.scrollHeight - prevScrollHeight;
        });
      })
      .catch(() => {
        if (seq === this.loadSeq) {
          this.loadingMore = false;
          // OnPush：请求失败续体非模板事件，loadingMore 需主动标记
          this.cdr.markForCheck();
        }
      });
  }

  /** 点击头像回复 */
  onAvatarClick(message: Message) {
    this.avatarClick.emit(message);
  }

  /** 整行点击：批量模式下切换该条选中（非批量模式下无副作用，避免误触） */
  onMessageRowClick(message: Message) {
    if (this.selectionMode) {
      this.toggleMessageSelected(message.id);
    }
  }

  /** 进入批量选择模式（父组件批量删除入口命令式调用，非本组件模板事件，需主动标记） */
  enterSelectionMode() {
    this.selectionMode = true;
    this.selectedIds.clear();
    this.cdr.markForCheck();
  }

  /** 退出批量选择模式并清空勾选（父组件删除成功后命令式调用，非本组件模板事件，需主动标记） */
  exitSelectionMode() {
    this.selectionMode = false;
    this.selectedIds.clear();
    this.cdr.markForCheck();
  }

  /** 单条消息勾选切换 */
  toggleMessageSelected(id: number) {
    if (this.selectedIds.has(id)) {
      this.selectedIds.delete(id);
    } else {
      this.selectedIds.add(id);
    }
  }

  /** 全选/取消全选（父组件批量条命令式调用，非本组件模板事件，需主动标记） */
  toggleSelectAll() {
    if (this.allSelected) {
      this.selectedIds.clear();
    } else {
      this.selectedIds = new Set(this.messages.map(m => m.id));
    }
    this.cdr.markForCheck();
  }

  /** 消息按 createAt 升序（旧上最新下） */
  private sortAsc(list: Message[]): Message[] {
    return [...list].sort((a, b) => this.timeOf(a) - this.timeOf(b));
  }

  /** createAt 时间戳（字符串或数字兜底） */
  private timeOf(m: Message): number {
    return new Date(m.createAt).getTime() || 0;
  }
}
